import React from "react";
import { Link } from "react-router-dom";
import Helmet from "react-helmet";
import { ReactComponent as AppleStoreSVG } from "../assets/store-badges/apple-store.svg";
import { ReactComponent as GooglePlaySVG } from "../assets/store-badges/google-play.svg";
import {
  brandIconPath,
  buildBreadcrumbSchema,
  buildWebPageSchema,
  pageUrls,
  publisherSchema,
  siteName,
  socialImageAlt,
  socialImageUrl,
  themeColor,
  translateAppIconUrl,
  translateAppStoreUrl,
  translateHeroPath,
  translatePlayStoreUrl,
} from "../seo";
import styles from "./TranslateApp.module.css";

const pageTitle = "Translate Text & Voice | 100+ Language Voice Translator App";
const pageDescription =
  "Download Translate Text & Voice by OmniMinds for instant text translation, voice translation, phrasebook access, favorites, pronunciation, and 100+ languages.";
const shortPageDescription =
  "A simple text and voice translator with phrasebook, favorites, pronunciation, and 100+ languages.";
const productImageAlt =
  "Abstract visual of a text and voice translation app with phone screens and travel-focused translation tools.";

const highlights = [
  {
    value: "100+",
    label: "languages",
  },
  {
    value: "Voice",
    label: "real-time translation",
  },
  {
    value: "Phrasebook",
    label: "travel-ready phrases",
  },
];

const features = [
  {
    title: "Instant text translation",
    description:
      "Translate phrases, notes, chats, and everyday writing quickly.",
  },
  {
    title: "Voice translation",
    description:
      "Speak, listen, and hear clear pronunciation when typing is too slow.",
  },
  {
    title: "Phrasebook and favorites",
    description:
      "Keep common phrases and saved translations ready for reuse.",
  },
  {
    title: "Dialects and offline phrasecards",
    description:
      "Use practical language support across regions and travel moments.",
  },
];

function TranslateApp() {
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${pageUrls.translateApp}#software`,
    name: "Translate Text & Voice",
    applicationCategory: "ProductivityApplication",
    operatingSystem: "iOS, Android",
    url: pageUrls.translateApp,
    image: translateAppIconUrl,
    screenshot: `${pageUrls.home}brand/translate-app-hero.png`,
    publisher: publisherSchema,
    downloadUrl: [translateAppStoreUrl, translatePlayStoreUrl],
    installUrl: [translateAppStoreUrl, translatePlayStoreUrl],
    description:
      "Translate Text & Voice is an OmniMinds app for instant text translation, voice translation, phrasebook workflows, favorites, pronunciation, multi-dialect support, and 100+ languages.",
  };

  const webPageSchema = buildWebPageSchema({
    url: pageUrls.translateApp,
    name: pageTitle,
    description: pageDescription,
    image: socialImageUrl,
    imageAlt: socialImageAlt,
  });

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: "Home", url: pageUrls.home },
    { name: "Translate Text & Voice", url: pageUrls.translateApp },
  ]);

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="application-name" content={siteName} />
        <meta name="apple-mobile-web-app-title" content={siteName} />
        <meta name="theme-color" content={themeColor} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrls.translateApp} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:title" content="Translate Text & Voice | OmniMinds" />
        <meta property="og:description" content={shortPageDescription} />
        <meta property="og:url" content={pageUrls.translateApp} />
        <meta property="og:image" content={socialImageUrl} />
        <meta property="og:image:alt" content={socialImageAlt} />
        <meta property="og:image:width" content="1672" />
        <meta property="og:image:height" content="941" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Translate Text & Voice | OmniMinds" />
        <meta name="twitter:description" content={shortPageDescription} />
        <meta name="twitter:image" content={socialImageUrl} />
        <meta name="twitter:image:alt" content={socialImageAlt} />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <header className={styles.header}>
        <Link to="/" className={styles.brand} aria-label="OmniMinds home">
          <img src={brandIconPath} alt="" width="32" height="32" />
          <span>OmniMinds</span>
        </Link>
        <nav className={styles.navLinks} aria-label="Translate Text & Voice navigation">
          <a href="#features">Features</a>
          <a href="#download">Download</a>
          <a href="https://chatculture.app" target="_blank" rel="noopener noreferrer">
            ChatCulture
          </a>
          <Link to="/">Home</Link>
        </nav>
      </header>

      <main>
        <section className={styles.hero}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>OmniMinds translation utility</p>
            <h1>Translate what you need, then keep moving.</h1>
            <p className={styles.heroText}>
              A simple OmniMinds translator for text, voice, pronunciation, saved phrases,
              favorites, and 100+ languages.
            </p>
            <div className={styles.heroActions}>
              <a href={translateAppStoreUrl} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store">
                <AppleStoreSVG className={styles.storeIcon} />
              </a>
              <a href={translatePlayStoreUrl} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play">
                <GooglePlaySVG className={styles.storeIcon} />
              </a>
            </div>
          </div>

          <div className={styles.productVisual}>
            <img
              src={translateHeroPath}
              alt={productImageAlt}
              width="1672"
              height="941"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>
        </section>

        <section className={styles.stats} aria-label="App highlights">
          {highlights.map((item) => (
            <div key={`${item.value}-${item.label}`}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </section>

        <section className={styles.featuresSection} id="features">
          <div className={styles.sectionIntro}>
            <p className={styles.eyebrow}>Simple by design</p>
            <h2>A focused translator for travel, learning, and quick conversations.</h2>
          </div>
          <div className={styles.featureGrid}>
            {features.map((feature) => (
              <article className={styles.featureCard} key={feature.title}>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.trustSection}>
          <div>
            <p className={styles.eyebrow}>Focused utility</p>
            <h2>Built for fast, everyday translation.</h2>
          </div>
          <p>
            Translate Text & Voice keeps the workflow direct: enter text, speak, listen,
            save phrases, and move on.
          </p>
        </section>

        <section className={styles.downloadSection} id="download">
          <div>
            <p className={styles.eyebrow}>Download</p>
            <h2>Get Translate Text & Voice on iOS or Android.</h2>
          </div>
          <div className={styles.downloadButtons}>
            <a href={translateAppStoreUrl} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store">
              <AppleStoreSVG className={styles.storeIcon} />
            </a>
            <a href={translatePlayStoreUrl} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play">
              <GooglePlaySVG className={styles.storeIcon} />
            </a>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div>
          <Link to="/" className={styles.footerBrand}>
            OmniMinds
          </Link>
          <p>A focused OmniMinds app for everyday translation.</p>
        </div>
        <nav aria-label="Footer navigation">
          <a href="https://chatculture.app" target="_blank" rel="noopener noreferrer">
            ChatCulture
          </a>
          <Link to="/terms">Terms and Conditions</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <a href="mailto:contact@omniminds.org">Contact Us</a>
        </nav>
        <span>Copyright {new Date().getFullYear()} Translate Text & Voice. All rights reserved.</span>
      </footer>
    </div>
  );
}

export default TranslateApp;
